import React, { useState } from 'react';
import { apiService } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    company: '',
    email: '',
    projectType: 'cloud_migration',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorDetails, setErrorDetails] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorDetails(null);

    console.log('Form submission started');
    console.log('Form data:', formData);

    // Client-side validation
    if (!formData.message || formData.message.trim().length < 10) {
      setSubmitStatus('error');
      setErrorDetails({
        message: 'Please provide more details about your project (minimum 10 characters).',
        status: 'client-validation',
        data: { message: ['Please provide more details about your project (minimum 10 characters).'] }
      });
      setIsSubmitting(false);
      return;
    }

    try {
      console.log('Calling apiService.submitContact...');
      const response = await apiService.submitContact(formData);
      console.log('API response:', response);
      setSubmitStatus('success');
      setFormData({
        fullName: '',
        company: '',
        email: '',
        projectType: 'cloud_migration',
        message: ''
      });
    } catch (error) {
      console.error('Contact form submission error:', error);
      const errorInfo = {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: error.config?.url,
        method: error.config?.method
      };
      console.error('Error details:', errorInfo);
      setErrorDetails(errorInfo);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">
            Ready to Transform Your Infrastructure?
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Let's discuss your technology challenges and architect a solution that scales with your business growth.
          </p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="space-y-8">
              <div className="flex items-start">
                <div className="bg-primary w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="fas fa-map-marker-alt text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Global Headquarters</h3>
                  <p className="text-gray-300">
                    Leicester, UK
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-primary w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="fas fa-phone text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Phone</h3>
                  <p className="text-gray-300">
                    +44 7424 892428<br />
                    24/7 Support Available
                  </p>
                </div>
              </div>
              
              <div className="flex items-start">
                <div className="bg-primary w-12 h-12 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="fas fa-envelope text-white"></i>
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-1">Enterprise Solutions</h3>
                  <p className="text-gray-300">
                    solutions@mtclicks.com<br />
                    Response within 2 hours
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-8">
            <h3 className="text-2xl font-semibold mb-6">Schedule a Technical Consultation</h3>
            
            {submitStatus === 'success' && (
              <div className="bg-green-600 text-white p-4 rounded-lg mb-6">
                Thank you! Your consultation request has been submitted successfully. We'll contact you shortly.
              </div>
            )}
            
            {submitStatus === 'error' && (
              <div className="bg-red-600 text-white p-4 rounded-lg mb-6">
                <p>Sorry, there was an error submitting your request. Please try again or contact us directly.</p>
                {errorDetails && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-sm">Error Details (click to expand)</summary>
                    <pre className="text-xs mt-2 bg-red-700 p-2 rounded overflow-auto">
                      {JSON.stringify(errorDetails, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Full Name</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Enterprise Corp"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="john@enterprise.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Project Requirements</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="cloud_migration">Cloud Migration & Modernization</option>
                  <option value="custom_development">Custom Application Development</option>
                  <option value="devops_automation">DevOps & Automation</option>
                  <option value="security_audit">Security & Compliance Audit</option>
                  <option value="infrastructure_optimization">Infrastructure Optimization</option>
                  <option value="architecture_review">Enterprise Architecture Review</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Project Details
                  <span className="text-xs text-gray-400 ml-2">
                    ({formData.message.length}/10 characters minimum)
                  </span>
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  required
                  className={`w-full px-4 py-3 bg-gray-700 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    formData.message.length > 0 && formData.message.length < 10 
                      ? 'border-red-500' 
                      : 'border-gray-600'
                  }`}
                  placeholder="Describe your technical requirements, current infrastructure, and business objectives..."
                ></textarea>
                {formData.message.length > 0 && formData.message.length < 10 && (
                  <p className="text-red-400 text-sm mt-1">
                    Please provide at least 10 characters ({10 - formData.message.length} more needed)
                  </p>
                )}
              </div>
              
              <button
                type="submit"
                disabled={isSubmitting || formData.message.trim().length < 10}
                className="w-full bg-primary hover:bg-blue-700 disabled:bg-blue-800 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {isSubmitting ? 'Submitting...' : 'Request Technical Consultation'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
